"use client";
import React, { useMemo, useRef, useState, useEffect } from "react";
import CommonTaile from "../Common/CommonTaile";
import { useTranslations } from "next-intl";
import ApplicationCard from "./ApplicationCard";

interface TabData {
	id: string;
	label: string;
	slug: string;
	children: Array<{
		id: string;
		name: string;
		slug: string;
		image: string;
	}>;
}

interface ApplicationsPageProps {
	tabsData: TabData[];
}

/** 仅进入视口后才渲染并加载资源，未进入视口不挂载、不加载图片/gif/接口；rootMargin 提前 200px 开始加载 */
function LazyApplicationCard({
	category,
	parentSlug,
	index,
}: {
	category: TabData["children"][0];
	parentSlug: string;
	index: number;
}) {
	const ref = useRef<HTMLDivElement>(null);
	const [isInView, setIsInView] = useState(false);

	useEffect(() => {
		const el = ref.current;
		if (!el) return;
		const observer = new IntersectionObserver(
			([entry]) => {
				if (entry.isIntersecting) setIsInView(true);
			},
			{ rootMargin: "200px 0px", threshold: 0 }
		);
		observer.observe(el);
		return () => observer.disconnect();
	}, []);

	return (
		<div
			ref={ref}
			id={category.slug}
			className="min-h-[350px] sm:min-h-[450px] md:min-h-[550px] lg:min-h-[560px] scroll-mt-24"
		>
			{isInView ? (
				<ApplicationCard
					category={category}
					index={index}
					isLeft={index % 2 === 0}
					parentCategorySlug={parentSlug}
				/>
			) : null}
		</div>
	);
}

const ApplicationsPage = ({ tabsData }: ApplicationsPageProps) => {
	const t = useTranslations();

	const sortedTabsData = useMemo(() => {
		return [...tabsData].sort((a, b) => {
			const aId = parseInt(a.id);
			const bId = parseInt(b.id);
			if (!isNaN(aId) && !isNaN(bId)) return aId - bId;
			return a.id.localeCompare(b.id);
		});
	}, [tabsData]);

	// 扁平化后，减少首屏请求与渲染
	const flatCategories = useMemo(() => {
		const list: { category: TabData["children"][0]; parentSlug: string; index: number }[] = [];
		let idx = 0;
		sortedTabsData.forEach((tab) => {
			(tab.children || []).forEach((category) => {
				list.push({ category, parentSlug: tab.slug, index: idx++ });
			});
		});
		return list;
	}, [sortedTabsData]);

	const breadcrumbItems = useMemo(
		() => [
			{ label: t("menu.Home"), link: "/" },
			{ label: t("menu.applications"), link: "" },
		],
		[t]
	);

	return (
		<>
			<CommonTaile
				msg={t("menu.applications")}
				backgroundImage="/image/plsyy/application-banner.webp"
				breadcrumbItems={breadcrumbItems}
				pageSlug="applications"
			/>

			<div className="bg-[#f3f5f7] py-20 max-md:py-10">
				<div className="container mx-auto py-8 sm:py-12 md:py-16">
					{flatCategories.length > 0 ? (
						<div className="space-y-60 max-md:space-y-20">
							{flatCategories.map(({ category, parentSlug, index }) => (
								<LazyApplicationCard
									key={category.id}
									category={category}
									parentSlug={parentSlug}
									index={index}
								/>
							))}
						</div>
					) : (
						<div className="text-center py-12">
							<p className="text-gray-500 text-lg">{t("common.NoData")}</p>
						</div>
					)}
				</div>
			</div>
		</>
	);
};

export default ApplicationsPage;
